import axios from 'axios';
import { AppThunk } from './index';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  apiGetProjectLeads,
  apiGetProjectLead,
  apiSetLeadState,
  apiCreateProposal,
  apiCancelJob,
  apiReopenJob,
  apiDeleteEventReport,
  apiCreateEventReport,
  apiAcceptPermit,
  apiRejectPermit,
  apiRequestCompletePermit,
  apiCreateBooking,
  apiGetCheckoutState,
  apiAddJobSpecialDocument,
  apiGetJobChange,
} from '../api/agency-jobs/agency-jobs';
import { ApiNS } from 'app/api/api';
import { AppNS } from '..';
import { apiDeleteInvoice, apiGetInvoiceById, apiGetInvoices } from 'app/api/invoices/invoices';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';
import { apiAddDocument, apiDeleteDocument } from 'app/api/documents/documents';
import { getEmptyProposal } from 'app/api/agency-jobs/agency-jobs-parsers';
import { apiChangeAgencyProjectOwner } from 'app/api/client-bookings/client-bookings';
import { apiGetJobChanges } from 'app/api/job-changes/job-changes';

const POLLING_DELAY = 3000;

export interface AgencyJobsState {
  delay: number | null;
  page: number;
  perPage: number;
  search: string;
  searchBy: string;
  agencyCompanyId: string;
  projectType: string;
  totalEntries: number;
  items: Array<AppNS.ProjectLead>;
  currentLead: AppNS.ProjectLead | null;
  currentInvoice: AppNS.ProjectInvoice | null;
  schedulerPickedPermits: Array<AppNS.PickedPermit>;
  isLeadLoading: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  isLeadLoadingError: boolean;
  isDeleteEventLoading: boolean;
  isDeleteEventError: boolean;
  isCreateEventLoading: boolean;
  isCreateEventError: string;
  isLeadStateLoadingError: boolean;
  isLeadStateLoading: boolean;
  addedEventId: string;

  proposal: AppNS.LeadProposal;
  isProposalRatePicked: boolean;
  isProposalValid: boolean;
  isProposalValidChecked: boolean;
  proposalValidErrors: Array<string>;
  isProposalLoading: boolean;
  isProposalLoadingError: boolean;

  isAcceptingPermit: string;
  isAcceptingChange: string;
  isAcceptingPermitError: string;

  isCompletingPermit: string;
  isCompletingPermitError: boolean;

  isRejectingPermit: string;
  isRejectingPermitError: boolean;

  isCancellingJob: boolean;
  isCancellingJobError: boolean;

  detailsActionDone: boolean;
  proposalActionTouched: boolean;
  proposalWithBudget: boolean;

  isDeleteInvoiceLoading: boolean;
  isDeleteInvoiceError: boolean;

  isInvoiceLoaded: boolean;
  isInvoiceLoading: boolean;
  isInvoiceLoadingError: boolean;

  isFixedAcceptingInProgress: boolean;
  fixedAcceptingError: string;
  fixedLeadCheckoutId: string;
  isFixedLeadCheckoutDone: boolean;

  isAddingSpecialDoc: boolean;
  isAddingSpecialDocError: AppNS.Params | null;

  addedSpecialDocId: string;
  isReopeningJob: boolean;
  isChangingAgencyProjectOwner: boolean;
  createProposalError: AppNS.Params | null;
}

const initialState: AgencyJobsState = {
  delay: POLLING_DELAY,
  page: 1,
  perPage: 10,
  search: '',
  searchBy: '',
  agencyCompanyId: '',
  projectType: '',
  currentLead: null,
  currentInvoice: null,
  totalEntries: 0,
  items: [],
  schedulerPickedPermits: [],
  isLeadLoading: false,
  isLeadLoadingError: false,
  isDeleteEventLoading: false,
  isDeleteEventError: false,
  isCreateEventLoading: false,
  isCreateEventError: '',
  isLeadStateLoadingError: false,
  isLeadStateLoading: false,
  isLoaded: false,
  isLoading: false,
  isProposalRatePicked: false,
  proposal: getEmptyProposal(),
  isProposalValid: false,
  isProposalValidChecked: false,
  proposalValidErrors: [],
  isProposalLoading: false,
  isProposalLoadingError: false,
  isCancellingJob: false,
  isCancellingJobError: false,
  detailsActionDone: false,
  proposalActionTouched: false,
  proposalWithBudget: false,
  addedEventId: '',
  isAcceptingPermit: '',
  isAcceptingChange: '',
  isAcceptingPermitError: '',
  isRejectingPermit: '',
  isRejectingPermitError: false,
  isCompletingPermit: '',
  isCompletingPermitError: false,
  isDeleteInvoiceLoading: false,
  isDeleteInvoiceError: false,
  isInvoiceLoaded: false,
  isInvoiceLoading: false,
  isInvoiceLoadingError: false,
  isFixedAcceptingInProgress: false,
  fixedLeadCheckoutId: '',
  fixedAcceptingError: '',
  isFixedLeadCheckoutDone: false,
  isAddingSpecialDoc: false,
  isAddingSpecialDocError: null,
  addedSpecialDocId: '',
  isReopeningJob: false,
  isChangingAgencyProjectOwner: false,
  createProposalError: null,
};

function checkProposalValid(state: AgencyJobsState) {
  const proposal = state.proposal;
  const validationErrors = [] as Array<string>;

  if (proposal.budget === '') {
    validationErrors.push('Please set budget');
  }

  if (proposal.type === 'doee') {
    if ((!proposal.files || proposal.files.length === 0) && proposal.pricingPolicy === '' ) {
      validationErrors.push('Please upload your proposal or add details about your pricing policy');
    }
  }

  if (proposal.type === 'special' && proposal.pricingPolicy === '') {
    validationErrors.push('Please add details about your pricing policy');
  }

  if (proposal.type === 'regular' || !proposal.type) {
    // REGULAR PROJECT
    if (proposal.terms === '') {
      validationErrors.push('Please specify terms and conditions');
    }

    if (proposal.services === '') {
      validationErrors.push('Please describe proposed services');
    }

     if (!proposal.paymentArrangement) {
      validationErrors.push('Please select one of payment arrangements');
    }
  }

  state.proposalValidErrors = validationErrors;
  state.isProposalValid = validationErrors.length ? false : true;
}

function changeEventStateAfterDeleteInvoice(
  state: AgencyJobsState,
  lineItems: Array<AppNS.InvoiceLineItem>
) {
  lineItems.forEach((lineItem) => {
    if (state?.currentLead?.job?.eventReports) {
      const events = state.currentLead.job.eventReports;
      if (events) {
        state.currentLead.job.eventReports = events.map((event: AppNS.JobEventReport) => {
          if (event.id !== lineItem.reportId) {
            return event;
          } else {
            return {
              ...event,
              state: 'ready_to_invoice',
            };
          }
        });
      }
    }
  });
}

function getReportInspections(
  permits: Array<AppNS.PickedPermit>,
  inspectionType?: string,
): Array<AppNS.Inspection> {
  const output: Array<AppNS.Inspection> = [];
  const activePermits = permits.filter((p) => p.isOn);
  activePermits.forEach((p) => {
    p.inspections.forEach((insp: string) => {
      output.push({
        id: insp,
        permitInspectionId: insp,
        status: 'scheduled',
        state: 'scheduled',
        name: inspectionType || insp,
        inspectionType: inspectionType || '',
        dcraState: 'pending',
        dcraStateRejectionComment: '',
      } as AppNS.Inspection);
    });
  });

  return output;
}

function updateJobPermitsInspectionsWithStatus(
  state: AgencyJobsState,
  inspectionsIds: Array<string>,
  status: AppNS.InspectionStatus
) {
  if (state?.currentLead?.job?.permits) {
    state.currentLead.job.permits = state.currentLead.job.permits.map(
      (permit: AppNS.Permit) => {
        if (permit.inspections) {
          const inspections = permit.inspections.map(
            (insp: AppNS.Inspection) => {
              if (inspectionsIds.includes(insp.id)) {
                return { ...insp, status: status };
              }
              return insp;
            }
          );
          return { ...permit, inspections };
        }
        return permit;
      }
    );
  }
}

const agencyLeads = createSlice({
  name: 'agencyLeads',
  initialState,
  reducers: {
    resetCollection(state) {
      state.items = [];
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setCurrentParams(state, action: PayloadAction<AppNS.Params>) {
      state.page = action.payload.page;
      state.search = action.payload.search;
      state.searchBy = action.payload.searchBy;
      state.projectType = action.payload.projectType;
      state.agencyCompanyId = action.payload.agencyCompanyId;
    },
    getLeadsStart(state: AgencyJobsState) {
      state.isLoading = true;
    },
    getLeadsSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.GetProjectLeadsApiResponse>
    ) {
      // state.error = null;
      state.isLoading = false;
      state.items = action.payload.results;
      state.totalEntries = action.payload.totalEntries;
    },
    getLeadsFailed(state: AgencyJobsState, action: PayloadAction<string>) {
      state.items = [];
      // state.error = action.payload;
      state.isLoading = false;
    },
    getLeadStart(state: AgencyJobsState) {
      state.currentLead = null;
      state.isLoaded = false;
      state.isLeadLoading = true;
      state.isLeadLoadingError = false;
    },
    getCurrentLeadSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.ProjectLead>
    ) {
      state.isLeadLoadingError = false;
      state.isLeadLoading = false;
      state.currentLead = action.payload;
    },
    setCurrentLeadExpired(state: AgencyJobsState) {
      if (state.currentLead) {
        state.currentLead = { ...state.currentLead, state: 'expired' };
      }
    },
    getLeadFailed(state: AgencyJobsState) {
      state.isLeadLoadingError = true;
      state.isLeadLoading = false;
    },
    setLeadStateStart(state: AgencyJobsState) {
      state.isLeadStateLoading = true;
    },
    setLeadStateSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.ProjectLeadState>
    ) {
      state.isLeadStateLoading = false;
      if (state.currentLead) {
        state.currentLead.state = action.payload;
      }
    },
    setLeadStateFailed(state: AgencyJobsState) {
      state.isLeadStateLoadingError = true;
      state.isLeadStateLoading = false;
    },
    deleteJobEventStart(state: AgencyJobsState) {
      state.isDeleteEventError = false;
      state.isDeleteEventLoading = true;
    },
    deleteJobEventFailed(state: AgencyJobsState) {
      state.isDeleteEventError = true;
      state.isDeleteEventLoading = false;
    },
    deleteJobEventSuccess(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isDeleteEventError = false;
      state.isDeleteEventLoading = false;

      if (state?.currentLead?.job?.eventReports) {
        const events = state.currentLead.job.eventReports;
        if (events) {
          state.currentLead.job.eventReports = events.map((event: AppNS.JobEventReport) => {
            if (event.id !== action.payload) {
              return event;
            } else {
              updateJobPermitsInspectionsWithStatus(
                state,
                event.eventInspections.map(
                  (insp) => insp.permitInspectionId || ''
                ),
                'pending'
              );
              return { ...event, state: 'cancelled_by_agency' };
            }
          });
        }
      }
    },
    deleteJobInvoiceStart(state: AgencyJobsState) {
      state.isDeleteInvoiceError = false;
      state.isDeleteInvoiceLoading = true;
    },
    deleteJobInvoiceFailed(state: AgencyJobsState) {
      state.isDeleteInvoiceError = true;
      state.isDeleteInvoiceLoading = false;
    },
    deleteJobInvoiceSuccess(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isDeleteInvoiceError = false;
      state.isDeleteInvoiceLoading = false;

      if (state?.currentLead?.invoices) {
        const invoices = state.currentLead.invoices;
        if (invoices) {
          state.currentLead.invoices = invoices.map((inv) => {
            if (inv.id !== action.payload) {
              return inv;
            } else {
              changeEventStateAfterDeleteInvoice(state, inv.lineItems);
              return { ...inv, state: 'deleted' };
            }
          });
        }
      }
    },
    createJobEventStart(state: AgencyJobsState) {
      state.isCreateEventError = '';
      state.isCreateEventLoading = true;
      // setAddedEventId('');
    },
    createJobEventFailed(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isCreateEventError = action.payload;
      state.isCreateEventLoading = false;
    },
    createJobEventSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.JobEventReport>
    ) {
      state.isCreateEventError = '';
      state.isCreateEventLoading = false;

      if (state?.currentLead?.job?.eventReports) {
        state.currentLead.job.eventReports = [
          ...state.currentLead.job.eventReports,
          action.payload,
        ];
      }
    },
    updateJobPermits(
      state: AgencyJobsState,
      action: PayloadAction<{
        inspectionsIds: Array<string>;
        status: AppNS.InspectionStatus;
      }>
    ) {
      updateJobPermitsInspectionsWithStatus(
        state,
        action.payload.inspectionsIds,
        action.payload.status
      );
    },
    setAddedEventId(state: AgencyJobsState, action: PayloadAction<string>) {
      state.addedEventId = action.payload;
    },
    setIsProposalValidChecked(state: AgencyJobsState) {
      checkProposalValid(state);
      state.isProposalValidChecked = true;
    },
    resetProposal(
      state: AgencyJobsState,
      action: PayloadAction<{
        id: string;
        visits: Array<AppNS.LeadProposalVisit>;
        files: Array<FileInfo>;
        specialInspections: AppNS.LeadProposalSpecialInspections | undefined;
        type: AppNS.ProjectType;
        paymentArrangement: AppNS.PaymentArrangement;
      }>
    ) {
      state.proposal = {
        ...getEmptyProposal(),
        ...action.payload,
      };

      // reset validation for new proposal
      state.isProposalValid = false;
      state.isProposalValidChecked = false;
      state.proposalValidErrors = [];
    },
    setProposalBudget(state: AgencyJobsState, action: PayloadAction<string>) {
      state.proposal.budget = action.payload;
      checkProposalValid(state);
    },
    setProposalSpecialInspections(
      state: AgencyJobsState,
      action: PayloadAction<{ permitId: string; inspections: Array<string> }>
    ) {
      if (!state.proposal.specialInspections) {
        state.proposal.specialInspections = {};
      }
      state.proposal.specialInspections[action.payload.permitId] =
        action.payload.inspections;

      checkProposalValid(state);
    },
    setProposalPaymentArrangement(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.PaymentArrangement>
    ) {
      state.proposal.paymentArrangement = action.payload;
      checkProposalValid(state);
    },
    setProposalMessage(
      state: AgencyJobsState,
      action: PayloadAction<{ type: string; value: string }>
    ) {
      if (action.payload.type === 'terms') {
        state.proposal.terms = action.payload.value;
      }

      if (action.payload.type === 'services') {
        state.proposal.services = action.payload.value;
      }

      if (action.payload.type === 'pricingPolicy') {
        state.proposal.pricingPolicy = action.payload.value;
      }

      if (action.payload.type === 'agencyId') {
        state.proposal.agencyId = action.payload.value;
      }
      checkProposalValid(state);
    },
    setProposalVisit(
      state: AgencyJobsState,
      action: PayloadAction<{ id: string; value: string; permitNumber: string }>
    ) {
      // if event exists
      const found = state.proposal.visits.find(
        (visit) => visit.id === action.payload.id
      );

      if (found) {
        state.proposal.visits = state.proposal.visits.map((visit) => {
          if (visit.id === action.payload.id) {
            return {
              ...visit,
              visits: action.payload.value,
              permitNumber: action.payload.permitNumber,
            };
          }
          return visit;
        });
      } else {
        state.proposal.visits.push({
          id: action.payload.id,
          visits: action.payload.value,
          permitNumber: action.payload.permitNumber,
        });
      }

      checkProposalValid(state);
    },
    setCreateProposalStart(state: AgencyJobsState) {
      state.isProposalLoading = true;
      state.createProposalError = null;
    },
    setCreateProposalSuccess(state: AgencyJobsState) {
      state.isProposalLoading = false;
      state.createProposalError = null;
      state.proposal.isSend = true;
      if (state.currentLead) {
        state.currentLead.state = 'proposal_sent';
      }
    },
    setCreateProposalFailed(state: AgencyJobsState, action: PayloadAction<AppNS.Params>) {
      state.isProposalLoadingError = true;
      state.createProposalError = action.payload;
      state.isProposalLoading = false;
    },
    setSchedulerPickedPermit(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.PickedPermit>
    ) {
      let currentPickedPermits = state.schedulerPickedPermits;
      if (currentPickedPermits) {
        const result = currentPickedPermits.find(
          (item) => item.id === action.payload.id
        );
        if (!result) {
          currentPickedPermits.push(action.payload);
        } else {
          currentPickedPermits = currentPickedPermits.map((item) => {
            if (item.id === action.payload.id) {
              return action.payload;
            }
            return item;
          });
        }
        state.schedulerPickedPermits = currentPickedPermits;
      }
    },
    resetSchedulerPickedPermit(state: AgencyJobsState) {
      state.schedulerPickedPermits = [];
      state.isCreateEventError = '';
    },
    cancelJobStart(state: AgencyJobsState) {
      state.isCancellingJob = true;
    },
    reopenJobStart(state: AgencyJobsState) {
      state.isReopeningJob = true;
    },
    reopenJobSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.isReopeningJob = false;
      if (state.currentLead && state.currentLead.job && action.payload.properties.state === 'confirmed_booking') {
        state.currentLead.job.state = 'confirmed_booking';
        state.currentLead.state = 'confirmed';
      }
    },
    cancelJobSuccess(
      state: AgencyJobsState,
      action: PayloadAction<{ id: string; requestOnly: boolean }>
    ) {
      state.isCancellingJob = false;

      if (action.payload.requestOnly) {
        if (state.currentLead?.job) {
          state.currentLead.job.cancellationMessage = 'cancelled';
        }
      } else {
        if (state.currentLead) {
          state.currentLead.state = 'cancelled_by_agency';
        }
      }
    },
    cancelJobFailed(state: AgencyJobsState) {
      state.isCancellingJobError = true;
      state.isCancellingJob = false;
    },
    setDetailsActionDone(
      state: AgencyJobsState,
      action: PayloadAction<boolean>
    ) {
      state.detailsActionDone = action.payload;
    },
    setProposalActionTouched(
      state: AgencyJobsState,
      action: PayloadAction<boolean>
    ) {
      state.proposalActionTouched = action.payload;
    },

    acceptPermitStart(state: AgencyJobsState, action: PayloadAction<string>) {
      state.isAcceptingPermit = action.payload;
      state.isAcceptingPermitError = '';
    },
    acceptChangeStart(state: AgencyJobsState, action: PayloadAction<string>) {
      state.isAcceptingChange = action.payload;
      state.isAcceptingPermitError = '';
    },
    acceptPermitSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.JobChange>
    ) {
      if (state.currentLead && state.currentLead.job?.jobChanges) {
        let currentPermit: AppNS.Permit | undefined;
        state.currentLead.job.jobChanges =
          state.currentLead.job?.jobChanges.map((item) => {
            if (item.id === action.payload.id) {
              currentPermit = item.permit as AppNS.Permit;
              return { ...item, state: action.payload.state };
            }
            return item;
          });
        if (action.payload.state === 'accepted' && currentPermit) {
          state.isAcceptingPermit = '';
          state.delay = null;
          state.currentLead.job.permits.push(currentPermit);
          state.currentLead.job.permitNumbers.push(currentPermit?.permitNumber);
          state.currentLead.proposal?.visits.push({
            id: currentPermit.id,
            visits: '-',
            permitNumber: currentPermit.permitNumber,
          });
        } else if (action.payload.state === 'accepting_failed' && action.payload.acceptingError && currentPermit) {
          state.isAcceptingPermit = '';
          state.isAcceptingChange = '';
          state.delay = POLLING_DELAY;
          state.isAcceptingPermitError = action.payload.acceptingError;
        }
      }
    },
    resetPermitAccepting(state: AgencyJobsState) {
      state.isAcceptingPermit = '';
      state.isAcceptingChange = '';
      state.isAcceptingPermitError = '';
      state.delay = POLLING_DELAY;
    },
    acceptPermitFailed(state: AgencyJobsState, action: PayloadAction<string>) {
      state.isAcceptingPermitError = action.payload;
      state.isAcceptingPermit = '';
      state.delay = POLLING_DELAY;
      state.isAcceptingChange = '';
    },

    requestCompletePermitStart(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isCompletingPermit = action.payload;
      state.isCompletingPermitError = false;
    },
    requestCompletePermitSuccess(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isCompletingPermit = '';
      if (state.currentLead && state.currentLead.job?.permits) {
        state.currentLead.job.permits = state.currentLead.job?.permits.map(
          (item) => {
            if (item.id === action.payload) {
              return { ...item, state: 'closing_requested' };
            }
            return item;
          }
        );
      }

      if (state.currentLead && state.currentLead.job?.jobChanges) {
        state.currentLead.job.jobChanges =
          state.currentLead.job?.jobChanges.map((item) => {
            if (item.permit.id === action.payload) {
              return {
                ...item,
                permit: { ...item.permit, state: 'closing_requested' },
              };
            }
            return item;
          });
      }
    },
    requestCompletePermitFailed(state: AgencyJobsState) {
      state.isCompletingPermitError = true;
      state.isCompletingPermit = '';
    },

    rejectPermitStart(state: AgencyJobsState, action: PayloadAction<string>) {
      state.isRejectingPermit = action.payload;
      state.isRejectingPermitError = false;
    },
    rejectPermitSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.Permit>
    ) {
      state.isRejectingPermit = '';

      if (state.currentLead && state.currentLead.job?.jobChanges) {
        state.currentLead.job.jobChanges =
          state.currentLead.job?.jobChanges.map((item) => {
            if (item.permit.id === action.payload.id) {
              return { ...item, state: 'rejected' };
            }
            return item;
          });
      }
    },
    rejectPermitFailed(state: AgencyJobsState) {
      state.isRejectingPermitError = true;
      state.isRejectingPermit = '';
    },
    getInvoiceStart(state: AgencyJobsState) {
      state.currentInvoice = null;
      state.isInvoiceLoaded = false;
      state.isInvoiceLoading = true;
      state.isInvoiceLoadingError = false;
    },
    getCurrentInvoiceSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.ProjectInvoice>
    ) {
      state.isInvoiceLoadingError = false;
      state.isInvoiceLoading = false;
      state.currentInvoice = action.payload;
    },
    addProposalFile(
      state: AgencyJobsState,
      action: PayloadAction<{ file: FileInfo }>
    ) {
      if (state.proposal) {
        state.proposal.files = [
          ...(state.proposal.files || []),
          action.payload.file,
        ];
        checkProposalValid(state);
      }
    },
    removeProposalImageStatus(
      state: AgencyJobsState,
      action: PayloadAction<{ imageId: string; status: AppNS.Params }>
    ) {
      if (state.proposal) {
        state.proposal.files = (state.proposal.files || []).map(
          (image: FileInfo) => {
            if (image.id === action.payload.imageId) {
              return { ...image, ...action.payload.status };
            }
            return image;
          }
        );
      }
    },

    acceptFixedLeadStart(state: AgencyJobsState) {
      state.fixedAcceptingError = '';
      state.isFixedAcceptingInProgress = true;
      state.isFixedLeadCheckoutDone = false;
      state.fixedLeadCheckoutId = '';
    },
    acceptFixedLeadFailed(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isFixedAcceptingInProgress = false;
      state.fixedAcceptingError = action.payload;
    },
    acceptFixedLeadSuccess(
      state: AgencyJobsState,
      action: PayloadAction<string>
    ) {
      state.isFixedAcceptingInProgress = false;
      state.isFixedLeadCheckoutDone = false;

      if (state.currentLead) {
        state.fixedLeadCheckoutId = action.payload;
        state.fixedAcceptingError = '';
      }
    },
    checkoutLeadStart(state: AgencyJobsState) {
      state.fixedAcceptingError = '';
    },
    checkoutLeadFailed(state: AgencyJobsState, action: PayloadAction<string>) {
      state.fixedAcceptingError = action.payload;
    },
    checkoutLeadSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.Params>
    ) {
      // if (state.currentLead && state.currentLead.job) {
        if (action.payload.state === 'completed') {
          state.isFixedLeadCheckoutDone = true;
        } else if (action.payload.state === 'failed') {
          state.fixedAcceptingError = action.payload.errorMessage;
        }
      // }
    },
    checkoutReset(state: AgencyJobsState) {
      state.fixedAcceptingError = '';
      state.fixedLeadCheckoutId = '';
      state.isFixedLeadCheckoutDone = false;
    },
    addSpecialDocStart(state: AgencyJobsState) {
      state.isAddingSpecialDoc = true;
      state.isAddingSpecialDocError = null;
    },
    addSpecialDocSuccess(
      state: AgencyJobsState,
      action: PayloadAction<{
        itemId: string;
        document: FileInfo;
        permitIds: string[];
        type: string;
      }>
    ) {
      state.isAddingSpecialDoc = false;
      state.addedSpecialDocId = action.payload.itemId;

      if (
        state.currentLead &&
        state.currentLead.job &&
        state.currentLead.job.jobDocuments
      ) {
        state.currentLead.job.jobDocuments.push({
          id: action.payload.itemId,
          document: action.payload.document,
          permitIds: action.payload.permitIds,
          type: action.payload.type,
          permitNumbers: action.payload.permitIds.map((permitId: string) => {
            return (
              state?.currentLead?.job?.permits.find(
                (permit: AppNS.Permit) => permit.id === permitId
              )?.permitNumber || ''
            );
          }),
        });
      }
    },
    addSpecialDocFailed(state: AgencyJobsState, action: PayloadAction<AppNS.Params>) {
      state.isAddingSpecialDocError = action.payload;
      state.isAddingSpecialDoc = false;
    },
    resetSpecialDocError(state: AgencyJobsState) {
      state.isAddingSpecialDocError = null;
      state.isAddingSpecialDoc = false;
    },
     changeProjectOwnerFailed(state: AgencyJobsState) {
      state.isChangingAgencyProjectOwner = false;
    },
    changeProjectOwnerStart(state: AgencyJobsState) {
      state.isChangingAgencyProjectOwner = true;
    },
    changeProjectOwnerSuccess(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.Params>
    ) {
      state.isChangingAgencyProjectOwner = false;
      if (state.currentLead && state.currentLead.job && state.currentLead.job.agency) {
        state.currentLead.job.agency.id = action.payload.id;
        state.currentLead.job.agency.firstName = action.payload.firstName;
        state.currentLead.job.agency.lastName = action.payload.lastName;
      }
    },
    setInvoicesforCurrentLead(
      state: AgencyJobsState,
      action: PayloadAction<ApiNS.InvoicesListResponse>
    ) {
      if (state.currentLead) {
        state.currentLead.invoices = action.payload.results as AppNS.ProjectInvoice[];
      }
    },
    setJobChangesforCurrentLead(
      state: AgencyJobsState,
      action: PayloadAction<AppNS.Params[]>
    ) {
      if (state.currentLead?.job) {
        state.currentLead.job.jobChanges = action.payload as AppNS.JobChange[];
      }
    },
  },
});

export const {
  cancelJobFailed,
  cancelJobStart,
  cancelJobSuccess,
  deleteJobEventFailed,
  deleteJobEventStart,
  deleteJobEventSuccess,
  createJobEventFailed,
  createJobEventStart,
  createJobEventSuccess,
  getCurrentLeadSuccess,
  getLeadFailed,
  getLeadsStart,
  getLeadsSuccess,
  getLeadStart,
  resetProposal,
  setCreateProposalFailed,
  setCreateProposalStart,
  setCreateProposalSuccess,
  setCurrentParams,
  setDetailsActionDone,
  setIsProposalValidChecked,
  setLeadStateFailed,
  setLeadStateStart,
  setLeadStateSuccess,
  setProposalVisit,
  setProposalMessage,
  setProposalBudget,
  setProposalSpecialInspections,
  setProposalPaymentArrangement,
  setProposalActionTouched,
  setSchedulerPickedPermit,
  resetSchedulerPickedPermit,
  setAddedEventId,
  acceptPermitStart,
  acceptChangeStart,
  acceptPermitSuccess,
  acceptPermitFailed,
  resetPermitAccepting,
  requestCompletePermitStart,
  requestCompletePermitSuccess,
  requestCompletePermitFailed,
  rejectPermitStart,
  rejectPermitSuccess,
  rejectPermitFailed,
  deleteJobInvoiceFailed,
  deleteJobInvoiceStart,
  deleteJobInvoiceSuccess,
  getInvoiceStart,
  getCurrentInvoiceSuccess,
  setCurrentLeadExpired,
  updateJobPermits,
  addProposalFile,
  removeProposalImageStatus,
  acceptFixedLeadStart,
  acceptFixedLeadFailed,
  acceptFixedLeadSuccess,
  checkoutLeadStart,
  checkoutLeadSuccess,
  checkoutLeadFailed,
  checkoutReset,
  addSpecialDocStart,
  addSpecialDocSuccess,
  addSpecialDocFailed,
  resetSpecialDocError,
  reopenJobStart,
  reopenJobSuccess,
  changeProjectOwnerFailed,
  changeProjectOwnerStart,
  changeProjectOwnerSuccess,
  setInvoicesforCurrentLead,
  setJobChangesforCurrentLead,
} = agencyLeads.actions;

export default agencyLeads.reducer;

export const getLeads =
  (
    type: string,
    searchQuery: string,
    page: number,
    searchBy: string,
    projectType: string,
    agencyCompanyId: string,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getLeadsStart());
      const result = await apiGetProjectLeads(
        type,
        searchQuery,
        page,
        searchBy,
        projectType,
        agencyCompanyId,
      );
      dispatch(getLeadsSuccess(result));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getLeadFailed());
    }
  };

export const getCurrentLead =
  (type: string, id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getLeadStart());
      const result = await apiGetProjectLead(type, id);

      dispatch(getCurrentLeadSuccess(result));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getLeadFailed());
    }
  };

export const setLeadState =
  (id: string, state: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLeadStateStart());
      const result = await apiSetLeadState({
        id,
        properties: {
          state: state,
        },
      });
      dispatch(setLeadStateSuccess(result));
      dispatch(setDetailsActionDone(true));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(setLeadStateFailed());
    }
  };

export const createProposal =
  (proposal: AppNS.LeadProposal): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setCreateProposalStart());

      await apiCreateProposal(proposal);
      dispatch(setCreateProposalSuccess());
      dispatch(setProposalActionTouched(true));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(setCreateProposalFailed(err.response.data));
    }
  };

export const deleteJobInvoice =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteJobInvoiceStart());
      await apiDeleteInvoice(id);
      dispatch(deleteJobInvoiceSuccess(id));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(deleteJobInvoiceFailed());
    }
  };

export const deleteEventReport =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteJobEventStart());
      await apiDeleteEventReport({ id });
      dispatch(deleteJobEventSuccess(id));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(deleteJobEventFailed());
    }
  };

export const createEventReport =
  (
    eventData: ApiNS.AddEventRequestData,
    reportAmount: number | null,
    inspectionType?: string,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setAddedEventId(''));
      dispatch(createJobEventStart());
      const response = await apiCreateEventReport(eventData);
      const eventInspections = getReportInspections(eventData.pickedPermits, inspectionType);
      if (response.id) {
        response.eventInspections = eventInspections;
        // set price for fixed projects
        if (reportAmount !== null) {
          response.amountForAgency = reportAmount;
          response.amountForClient = reportAmount;
        }
      }

      dispatch(setAddedEventId(response.id));
      dispatch(createJobEventSuccess(response));
      dispatch(
        updateJobPermits({
          inspectionsIds: eventInspections.map((i) => i.id),
          status: 'scheduled' as AppNS.InspectionStatus,
        })
      );
    } catch (message) {
      if (message) {
        dispatch(createJobEventFailed(message as string));
      }
    }
  };

export const cancelJob =
  (id: string, message: string, requestOnly: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(cancelJobStart());
      await apiCancelJob({ id, message }, requestOnly);
      dispatch(cancelJobSuccess({ id, requestOnly }));
      if (!requestOnly) {
        dispatch(setDetailsActionDone(true));
      }
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(cancelJobFailed());
    }
  };

export const reopenJob =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(reopenJobStart());
      const result = await apiReopenJob({ id });
      dispatch(reopenJobSuccess(result));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(cancelJobFailed());
    }
  };

  export const getJobChange =
  (changeId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(acceptChangeStart(changeId));
      const response = await apiGetJobChange(changeId);
      dispatch(acceptPermitSuccess(response));
    } catch (err) {
      if (err) {
        dispatch(acceptPermitFailed(err as string));
      }
    }
  };

export const acceptPermit =
  (changeId: string, permit: AppNS.Permit): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(acceptPermitStart(permit.id));
      dispatch(acceptChangeStart(changeId));
      const response = await apiAcceptPermit(changeId);
      dispatch(acceptPermitSuccess(response));
    } catch (err) {
      if (err) {
        dispatch(acceptPermitFailed(err as string));
      }
    }
  };

export const rejectPermit =
  (changeId: string, permit: AppNS.Permit): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(rejectPermitStart(permit.id));
      await apiRejectPermit(changeId);
      dispatch(rejectPermitSuccess(permit));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(rejectPermitFailed());
    }
  };

export const requestCompletePermit =
  (permitId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(requestCompletePermitStart(permitId));
      await apiRequestCompletePermit(permitId);
      dispatch(requestCompletePermitSuccess(permitId));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(requestCompletePermitFailed());
    }
  };

export const getCurrentInvoice =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getInvoiceStart());
      const result = await apiGetInvoiceById(id);

      dispatch(getCurrentInvoiceSuccess(result));
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getLeadFailed());
    }
  };

export const addProposalDocument =
  (leadId: string, imageInfo: FileInfo): AppThunk =>
  async (dispatch) => {
    try {
      const output = await apiAddDocument({
        properties: {
          type: 'proposal',
          jobLeadId: leadId,
          file: imageInfo.url || '',
        },
      });
      dispatch(
        addProposalFile({ file: { ...output, uppyId: imageInfo.uppyId } })
      );
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
    }
  };

export const removeProposalDocument =
  (imageId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(
        removeProposalImageStatus({ imageId, status: { isLoaded: false } })
      );
      await apiDeleteDocument(imageId);
      dispatch(
        removeProposalImageStatus({ imageId, status: { isRemoved: true } })
      );
    } catch (err) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
    }
  };

export const acceptFixedLead =
  (data: ApiNS.AddBookingRequestData): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(acceptFixedLeadStart());
      const response = await apiCreateBooking(data);
      dispatch(acceptFixedLeadSuccess(response));
    } catch (err) {
      if (err) {
        dispatch(acceptFixedLeadFailed(err as string));
      }
    }
  };

export const getInvoicesForCurrentBooking =
  (bookingId: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apiGetInvoices(bookingId, '', '', 1, 'booking_id', '', 50);
      dispatch(setInvoicesforCurrentLead(response));
    } catch (err) {
    }
  };

export const getJobChangesForCurrentBooking =
  (jobId: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apiGetJobChanges(jobId, 20);
      dispatch(setJobChangesforCurrentLead(response));
    } catch (err) {
    }
  };

export const getCheckoutState =
  (checkoutId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(checkoutLeadStart());
      const response = await apiGetCheckoutState(checkoutId);
      dispatch(checkoutLeadSuccess(response));
    } catch (err) {
      if (err) {
        dispatch(checkoutLeadFailed(err as string));
      }
    }
  };

export const addJobSpecialDocument =
  (jobId: string, document: FileInfo, permitIds: string[], type: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addSpecialDocStart());
      const output = await apiAddJobSpecialDocument({
        properties: {
          jobId,
          documentId: document.id || '',
          permitIds,
          type,
        },
      });
      dispatch(
        addSpecialDocSuccess({ itemId: output.id, document, permitIds, type })
      );
    } catch (err) {
      if (err) {
        dispatch(addSpecialDocFailed(err));
      }
    }
  };

export const resetSchedulerPickedPermits = (): AppThunk => async (dispatch) => {
  dispatch(resetSchedulerPickedPermit());
}

export const resetSpecialDocErrors = (): AppThunk => async (dispatch) => {
  dispatch(resetSpecialDocError());
}

export const changeAgencyProjectOwner =
  (id: string, agencyId: string, pm: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(changeProjectOwnerStart());
      await apiChangeAgencyProjectOwner(id, agencyId);
      dispatch(changeProjectOwnerSuccess(pm));
    } catch (err) {
      if (err) {
        dispatch(changeProjectOwnerFailed());
      }
    }
  };
